/**
 * Represents a collection of customer base.
 * @typedef {Object} CustomerBase
 * @property {string} id - The ID of the customer base.
 * @property {string} name - The name of the customer base.
 * @property {Object} prices - The prices of the customer base.
 * @property {Object} prices.cl - The prices in CL currency.
 * @property {string} prices.cl.currency - The currency of the CL price.
 * @property {number} prices.cl.price - The price in CL currency.
 * @property {number} prices.cl.trialDays - The number of trial days for the CL price.
 * @property {Object} prices.global - The prices in global currency.
 * @property {string} prices.global.currency - The currency of the global price.
 * @property {number} prices.global.price - The price in global currency.
 * @property {number} prices.global.trialDays - The number of trial days for the global price.
 */

/**
 * Represents a collection of products.
 * @type {CustomerBase[]}
 */
const customerBase = [
  {
    id: 'pro',
    description: 'Permite a tus clientes administrar su suscripción.',
    isCommingSoon: false,
    name: 'Pro',
    prices: {
      CL: {
        currency: 'UF',
        price: 1.5,
        trialDays: 7
      },
      global: {
        currency: 'USD',
        price: 65,
        trialDays: 7
      }
    }
  },
  {
    id: 'advanced',
    description: 'Brinda control total a tus clientes sobre su suscripción.',
    isCommingSoon: false,
    name: 'Advanced',
    prices: {
      CL: {
        currency: 'UF',
        price: 2.0,
        trialDays: 7
      },
      global: {
        currency: 'USD',
        price: 85,
        trialDays: 7
      }
    }
  }
]

/**
 * Represents a collection of payment links.
 *
 * @typedef {Object} PaymentLink
 * @property {string} id - The ID of the payment link.
 * @property {string} name - The name of the payment link.
 * @property {boolean} isCommingSoon - Indicates if the payment link is coming soon.
 * @property {Object} prices - The prices of the payment link.
 * @property {Object} prices.cl - The price details for Chile.
 * @property {string} prices.cl.currency - The currency for Chile.
 * @property {number} prices.cl.price - The price for Chile.
 * @property {number} prices.cl.trialDays - The number of trial days for Chile.
 * @property {Object} prices.global - The price details for global.
 * @property {string} prices.global.currency - The currency for global.
 * @property {number} prices.global.price - The price for global.
 * @property {number} prices.global.trialDays - The number of trial days for global.
 */

/**
 * An array of payment links.
 *
 * @type {PaymentLink[]}
 */
const paymentLinks = [
  {
    id: 'pro',
    description: 'Comienza a recibir pagos en minutos.',
    isCommingSoon: false,
    name: 'Pro',
    prices: {
      CL: {
        currency: 'UF',
        price: 1,
        trialDays: 7
      },
      global: {
        currency: 'USD',
        price: 45,
        trialDays: 7
      }
    }
  },
  {
    id: 'advanced',
    description: 'Obtén métricas sobre tasas de conversión, intentos de pago, pagos fallidos y más.',
    isCommingSoon: true,
    name: 'Advanced',
    prices: {
      CL: {
        currency: 'UF',
        price: 2.0,
        trialDays: 7
      },
      global: {
        currency: 'USD',
        price: 85,
        trialDays: 7
      }
    }
  }
]

/**
 * Represents a collection of signup forms.
 *
 * @typedef {Object} SignupForm
 * @property {string} id - The ID of the signup form.
 * @property {string} name - The name of the signup form.
 * @property {Object} prices - The prices of the signup form.
 * @property {Object} prices.cl - The prices in CLF currency.
 * @property {string} prices.cl.currency - The currency code for CLF.
 * @property {number} prices.cl.price - The price in CLF currency.
 * @property {number} prices.cl.trialDays - The number of trial days for CLF currency.
 * @property {Object} prices.global - The prices in USD currency.
 * @property {string} prices.global.currency - The currency code for USD.
 * @property {number} prices.global.price - The price in USD currency.
 * @property {number} prices.global.trialDays - The number of trial days for USD currency.
 */

/**
 * An array of signup forms.
 *
 * @type {SignupForm[]}
 */
const signupForms = [
  {
    id: 'pro',
    description: 'Acepta suscripciones, captura la información de facturación y la tarjeta de crédito de tus clientes.',
    isCommingSoon: false,
    name: 'Pro',
    prices: {
      CL: {
        currency: 'UF',
        price: 1.5,
        trialDays: 7
      },
      global: {
        currency: 'USD',
        price: 65,
        trialDays: 7
      }
    }
  },
  {
    id: 'advanced',
    description: 'Obtén métricas sobre tasas de conversión, pagos fallidos, volúmenes y más.',
    isCommingSoon: false,
    name: 'Advanced',
    prices: {
      CL: {
        currency: 'UF',
        price: 2.0,
        trialDays: 7
      },
      global: {
        currency: 'USD',
        price: 85,
        trialDays: 7
      }
    }
  }
]

export {
  customerBase,
  paymentLinks,
  signupForms
}